import { INTRO_OFFERS_ENABLED, ANNUAL_CHECKOUT_PATH } from "@/lib/introPassPath";
import { useMembershipOffer } from "@/hooks/useMembershipOffer";
import { signOutDestination } from "@/lib/signOutDestination";
import { useEffect, useState } from "react";
import { useNavigate } from "@/lib/router-compat";
import { supabase } from "@/integrations/supabase/client";
import { trackCtaClick } from "@/lib/ctaTracking";
import logoImg from "@/assets/landing/logo-96.webp";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
  DropdownMenuPortal,
} from "@/components/ui/dropdown-menu";
import { ChevronDown, Home, Map, Users, User as UserIcon, CreditCard, LayoutDashboard, LogOut, Menu, X, Brain, Mail, MessageCircle, Phone, Settings, BookOpen } from "lucide-react";
import type { User } from "@supabase/supabase-js";
import { useAccountMenuAccess } from "@/hooks/useAccountMenuAccess";
import { AccountMenuContent } from "@/components/AccountMenuContent";
import { useLocalPriceLabel } from "@/hooks/useLocalPriceLabel";
import {
  useIntroPassPlan,
  introPassPriceINR,
  INTRO_PASS_CHECKOUT_PATH,
} from "@/hooks/useIntroPassPlan";
import { warm } from "@/lib/linkPrefetch";

/**
 * Shared top navigation for non-landing public pages (Assessment, Courses, etc.).
 * Account dropdown is kept in sync with the landing page (LandingV2) so the user sees
 * the same items everywhere.
 */
export const PublicNav = () => {
  const navigate = useNavigate();
  const [authed, setAuthed] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [childProfile, setChildProfile] = useState<{ child_name: string } | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const { isAdmin, hasAdminAccess, hasPartnerAccess } = useAccountMenuAccess(user?.id);
  // CTA price and length come from the admin-configured all-access pass plan,
  // never a hardcoded amount.
  const { pass } = useIntroPassPlan();
  const { label: priceLabel } = useLocalPriceLabel();
  const passPrice = introPassPriceINR(pass);
  const membershipOffer = useMembershipOffer();
  const trialCtaLabel = !INTRO_OFFERS_ENABLED
    ? `Get Annual for ${membershipOffer.annualLabel}`
    : passPrice
    ? `Explore All 20 Courses for ${priceLabel(passPrice)}`
    : "See membership plans";

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setAuthed(!!session);
      setUser(session?.user ?? null);
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_e, s) => {
      setAuthed(!!s);
      setUser(s?.user ?? null);
    });
    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    if (!user?.id) { setChildProfile(null); return; }
    let cancelled = false;
    supabase
      .from("children")
      .select("child_name")
      .eq("parent_id", user.id)
      .limit(1)
      .maybeSingle()
      .then(({ data }) => {
        if (!cancelled && data?.child_name) setChildProfile({ child_name: data.child_name });
      });
    return () => { cancelled = true; };
  }, [user?.id]);

  const goFreeCourse = () => {
    void trackCtaClick("try-free-course");
    // Guests pay first and confirm their mobile number afterwards — the sign-in
    // wall was what ad traffic bounced off. Signed-in visitors just go in.
    if (authed) { navigate("/learning-journeys"); return; }
    navigate(!INTRO_OFFERS_ENABLED ? ANNUAL_CHECKOUT_PATH : passPrice ? INTRO_PASS_CHECKOUT_PATH : "/subscription");
  };
  const goAuth = () => navigate("/auth");
  const handleSignOut = async () => { await supabase.auth.signOut(); navigate(signOutDestination()); };
  const userInitial = (user?.user_metadata?.full_name as string)?.charAt(0)?.toUpperCase() || user?.email?.charAt(0)?.toUpperCase() || "L";
  const displayName = (user?.user_metadata?.full_name as string)?.split(" ")[0] || "Account";
  const childFirst = childProfile?.child_name.split(" ")[0];
  const close = () => setMenuOpen(false);


  return (
    <header className="w-full font-jakarta sticky top-0 z-[100] bg-background/95 backdrop-blur border-b border-border">
      <nav>
        <div className="max-w-[1280px] mx-auto px-4 lg:px-8 py-2.5 flex items-center justify-between gap-6">
          <a href="/" onClick={(e) => { e.preventDefault(); navigate("/"); }} className="flex items-center gap-3 shrink-0">
            <img loading="lazy" decoding="async" src={logoImg} alt="LeadXkids" width={34} height={34} className="w-[34px] h-[34px] object-contain" />
            <span className="text-[22px] font-bold tracking-tight bg-gradient-to-r from-[#087F7A] to-[#C94337] bg-clip-text text-transparent">LeadXkids</span>
          </a>

          <ul className="hidden lg:flex items-center gap-7 text-[15px] font-semibold">
            <li>
              <a
                href="/blog"
                onClick={(e) => { e.preventDefault(); navigate("/blog"); }}
                className="flex items-center gap-2 text-foreground hover:text-[#087F7A] transition-colors"
              >
                <BookOpen className="w-4 h-4" />
                Blog
              </a>
            </li>
          </ul>

          <div className="flex items-center gap-2 whitespace-nowrap">
            {!authed && (
              <button
                type="button"
                onClick={goFreeCourse}
                onPointerEnter={() => warm("/membership-checkout")}
                onTouchStart={() => warm("/membership-checkout")}
                onFocus={() => warm("/membership-checkout")}
                className="hidden sm:inline-flex shrink-0 whitespace-nowrap items-center px-4 py-2 rounded-lg bg-[#087F7A] hover:bg-[#066B67] text-white text-[14px] font-semibold transition-colors"
              >
                {trialCtaLabel}
              </button>
            )}

            {authed ? (
              <DropdownMenu>
                <DropdownMenuTrigger className="inline-flex items-center gap-2 px-1 py-1 rounded-full hover:bg-muted transition-colors min-w-0">
                  <div className="w-8 h-8 shrink-0 rounded-full bg-gradient-to-br from-[#087F7A] to-[#C94337] text-white text-[13px] font-bold flex items-center justify-center">{userInitial}</div>
                  <span className="hidden sm:inline text-[13px] font-medium text-foreground truncate max-w-[120px]">{displayName}</span>
                  <ChevronDown className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                </DropdownMenuTrigger>
                <AccountMenuContent
                  displayName={displayName}
                  email={user?.email}
                  childFirstName={childProfile?.child_name}
                  hasAdminAccess={hasAdminAccess}
                  isAdmin={isAdmin}
                  isInfluencer={hasPartnerAccess}
                  onSignOut={handleSignOut}
                />
              </DropdownMenu>
            ) : (
              <button type="button" className="text-[14px] font-medium text-foreground hover:text-[#087F7A] transition-colors" onClick={goAuth}>Sign in</button>
            )}

            {/* Signed-in parents already get Blog inside the account menu, so
                a second hamburger next to it is pure clutter.
                Guests still need it for Blog and the trial course CTA. */}
            {!authed && (
              <button
                type="button"
                className="lg:hidden inline-flex items-center justify-center min-w-11 min-h-11 -mr-2 text-foreground"
                aria-label={menuOpen ? "Close menu" : "Open menu"}
                aria-expanded={menuOpen}
                onClick={() => setMenuOpen((v) => !v)}
              >
                {menuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </button>
            )}
          </div>
        </div>

        {/* Mobile menu */}
        {menuOpen && !authed && (
          <div className="lg:hidden border-t border-border bg-background">
            <ul className="px-4 py-3 flex flex-col gap-1 text-[15px] font-semibold text-foreground">
              <li>
                <button
                  onClick={() => { close(); navigate("/blog"); }}
                  className="flex items-center gap-2 w-full text-left py-2 text-foreground hover:text-[#087F7A]"
                >
                  <BookOpen className="w-4 h-4" />
                  Blog
                </button>
              </li>
              {!authed && (
                <li>
                  <button
                    onClick={() => { close(); goFreeCourse(); }}
                    onPointerEnter={() => warm("/membership-checkout")}
                    onTouchStart={() => warm("/membership-checkout")}
                    onFocus={() => warm("/membership-checkout")}
                    className="mt-2 w-full py-2.5 rounded-lg bg-[#087F7A] text-white font-semibold"
                  >
                    {trialCtaLabel}
                  </button>
                </li>
              )}
            </ul>
          </div>
        )}
      </nav>
    </header>
  );
};

export default PublicNav;

